import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, setAccessToken, setSessionLostHandler } from './api';
import type { AuthUser, LoginResponse, LoginResult, TotpChallenge, UserRole } from './types';
import { isTotpChallenge } from './types';

interface AuthState {
  user: AuthUser | null;
  /** Эхний refresh дуусаагүй үед true — flash-аас сэргийлнэ */
  loading: boolean;
  /**
   * Нууц үгээр нэвтэрнэ. TOTP тохируулсан бол хэрэглэгч ОРООГҮЙ хэвээр
   * үлдэж, challenge буцна — дараа нь `loginTotp` дуудна.
   */
  login: (username: string, password: string) => Promise<TotpChallenge | null>;
  /** Хоёр дахь алхам — 6 оронтой код. */
  loginTotp: (challengeId: string, code: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Тухайн ролиудын аль нэг эсэх */
  can: (...roles: UserRole[]) => boolean;
  /**
   * Эрх байгаа эсэх — `"модуль.үйлдэл"` (`@boots/domain` → `permKey`).
   *
   * ⚠️ Энэ нь ХАМГААЛАЛТ БИШ, зөвхөн эмх цэгц: цэс, товч нуухад.
   *    Жинхэнэ шалгалт сервер дээр (`requirePermission`) — DevTools-оор
   *    энэ утгыг өөрчилсөн ч API 403 хэвээр өгнө.
   */
  hasPerm: (permission: string) => boolean;
  /** Захирал зөвхөн харна */
  readOnly: boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Хуудас сэргээхэд refresh cookie-гоор сессийг сэргээнэ
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // ⚠️ Энэ хүсэлт эхний render-ыг хаадаг (`loading` → App.tsx-ийн
        //    spinner). Хугацааны хязгааргүй бол холболтыг хүлээж авчхаад
        //    хариу буцаадаггүй прокси дээр «Сесс шалгаж байна…» МӨНХӨД
        //    эргэлдэнэ — browser fetch өөрөө таслахгүй. Таслагдвал catch
        //    ажиллаж нэвтрэх дэлгэц гарна. Гар утасны `lib/api.ts`-ийн
        //    TIMEOUT_MS-тэй ижил хугацаа.
        const res = await api<LoginResponse>('/auth/refresh', {
          method: 'POST',
          signal: AbortSignal.timeout(20_000),
        });
        if (!alive) return;
        setAccessToken(res.accessToken);
        setUser(res.user);
      } catch {
        if (alive) setUser(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Токен сэргээх боломжгүй болбол нэвтрэх дэлгэц рүү
  useEffect(() => {
    setSessionLostHandler(() => setUser(null));
    return () => setSessionLostHandler(null);
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    const res = await api<LoginResult>('/auth/login', {
      method: 'POST',
      body: { username, password },
    });
    if (isTotpChallenge(res)) return res;
    setAccessToken(res.accessToken);
    setUser(res.user);
    return null;
  }, []);

  const loginTotp = useCallback(async (challengeId: string, code: string) => {
    const res = await api<LoginResponse>('/auth/login/totp', {
      method: 'POST',
      body: { challengeId, code },
    });
    setAccessToken(res.accessToken);
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } finally {
      setAccessToken(null);
      setUser(null);
    }
  }, []);

  const value = useMemo<AuthState>(() => ({
    user,
    loading,
    login,
    loginTotp,
    logout,
    can: (...roles) => !!user && roles.includes(user.role),
    hasPerm: (permission) => !!user && user.permissions.includes(permission),
    readOnly: user?.role === 'DIRECTOR_READONLY',
  }), [user, loading, login, loginTotp, logout]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth-ыг AuthProvider дотор ашиглана');
  return ctx;
}
